import { stripeApi } from './stripe';
import { PRICES } from '../config/billing';
import { apiGet, apiPatch } from './apiClient';

/**
 * Called when a new Customer is created in Intrlock.
 * Creates a Stripe customer and saves the stripe_customer_id back to the customer record.
 */
export async function onCustomerCreated(customer: {
  id: number;
  company_name: string;
  billing_email?: string;
}) {
  try {
    const stripeCustomer = await stripeApi.createCustomer(
      customer.billing_email || '',
      customer.company_name,
      { intrlock_customer_id: String(customer.id) }
    );
    const stripeId = stripeCustomer.id;

    await apiPatch('customers', customer.id, { stripe_customer_id: stripeId });

    console.log(`[billing] Created Stripe customer ${stripeId} for customer #${customer.id}`);
    return stripeId;
  } catch (err) {
    console.error('[billing] Failed to create Stripe customer:', err);
    return null;
  }
}

/**
 * Called when a new Property is created.
 * Creates a subscription with property base + device qty + unit qty line items.
 */
export async function onPropertyCreated(
  property: { id: number | string; name: string; customer_id: number | string },
  deviceCount: number = 0,
  unitCount: number = 0
) {
  try {
    const customerData = await apiGet('customers', property.customer_id);
    const stripeCustomerId = customerData?.stripe_customer_id;

    if (!stripeCustomerId) {
      console.warn('[billing] Customer has no stripe_customer_id, skipping subscription');
      return null;
    }

    const items: Array<{ price: string; quantity: number }> = [
      { price: PRICES.PROPERTY_BASE, quantity: 1 },
    ];
    if (deviceCount > 0) {
      items.push({ price: PRICES.PER_DEVICE, quantity: deviceCount });
    }
    if (unitCount > 0) {
      items.push({ price: PRICES.PER_UNIT, quantity: unitCount });
    }

    const sub = await stripeApi.createSubscription(stripeCustomerId, items, {
      intrlock_property_id: String(property.id),
      intrlock_customer_id: String(property.customer_id),
      property_name: property.name,
    });

    await apiPatch('properties', property.id, { stripe_subscription_id: sub.id });

    console.log(`[billing] Created subscription ${sub.id} for property #${property.id}`);
    return sub.id;
  } catch (err) {
    console.error('[billing] Failed to create subscription for property:', err);
    return null;
  }
}

/**
 * Called when devices are added/removed from a property.
 * Updates the device line item quantity on the property's subscription.
 */
export async function onDevicesChanged(propertyId: number, newDeviceCount: number) {
  try {
    const propData = await apiGet('properties', propertyId);
    const subscriptionId = propData?.stripe_subscription_id;

    if (!subscriptionId) {
      console.warn('[billing] Property has no subscription, skipping device update');
      return;
    }

    const sub = await stripeApi.getSubscriptionDetail(subscriptionId);
    const deviceItem = sub.items?.data?.find(
      (item: any) => item.price?.id === PRICES.PER_DEVICE
    );

    if (deviceItem) {
      await stripeApi.updateSubscription(subscriptionId, [
        { id: deviceItem.id, quantity: Math.max(newDeviceCount, 0) },
      ]);
    } else if (newDeviceCount > 0) {
      console.warn('[billing] No device line item on subscription, cannot update quantity');
    }

    console.log(`[billing] Updated device count to ${newDeviceCount} for property #${propertyId}`);
  } catch (err) {
    console.error('[billing] Failed to update device count:', err);
  }
}

/**
 * Called when units are added/removed from a property.
 * Updates the unit line item quantity on the property's subscription.
 */
export async function onUnitsChanged(propertyId: number, newUnitCount: number) {
  try {
    const propData = await apiGet('properties', propertyId);
    const subscriptionId = propData?.stripe_subscription_id;

    if (!subscriptionId) {
      console.warn('[billing] Property has no subscription, skipping unit update');
      return;
    }

    const sub = await stripeApi.getSubscriptionDetail(subscriptionId);
    const unitItem = sub.items?.data?.find(
      (item: any) => item.price?.id === PRICES.PER_UNIT
    );

    if (unitItem) {
      await stripeApi.updateSubscription(subscriptionId, [
        { id: unitItem.id, quantity: Math.max(newUnitCount, 0) },
      ]);
    }

    console.log(`[billing] Updated unit count to ${newUnitCount} for property #${propertyId}`);
  } catch (err) {
    console.error('[billing] Failed to update unit count:', err);
  }
}
